import * as React from "react";
import ReactMarkdown from "react-markdown";
import { InlineCode } from "./InlineCode";
import {
  Link,
  ListItem,
  OrderedList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Stack,
  UnorderedList,
  chakra,
  useColorModeValue,
} from "@chakra-ui/react";
import { MarkdownCodeBlock } from "@apollo/chakra-helpers";
//prism themes after markdown code block
import "prismjs/components/prism-bash";
import "prismjs/components/prism-csharp";
import "prismjs/components/prism-diff";
import "prismjs/components/prism-graphql";
import "prismjs/components/prism-groovy";
import "prismjs/components/prism-java";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-json";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-tsx";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-yaml";
import { colors } from "@apollo/brand";
type KeyTermProps = {
  definition: string;
  term: string;
};
export const KeyTerm = ({ definition, term }: KeyTermProps): JSX.Element => {
  const { border, bg: popOverBG } = colors.tokens;
  const bg = useColorModeValue(
    popOverBG.secondary.base,
    colors.primitives.navy[500]
  );
  const borderColor = useColorModeValue(
    border.secondary.base,
    border.secondary.dark
  );
  return (
    <Popover
      onOpen={() => {
        window.gtag?.("event", "apollopedia_open", {
          term: term.toLowerCase(),
        });
      }}
    >
      <PopoverTrigger>
        <chakra.span
          sx={{
            textDecoration: "underline",
            textDecorationStyle: "dotted",
            textDecorationThickness: "1.5px",
            textDecorationColor: "gray.300",
            textUnderlineOffset: "0.2em",
            _dark: {
              textDecorationColor: "blue.400",
            },
          }}
          _hover={{ cursor: "help" }}
        >
          {term}
        </chakra.span>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          borderColor={borderColor}
          bg={bg}
          minW={"sm"}
          w={
            definition.length > 500
              ? "xl"
              : definition.length > 300
              ? "md"
              : "sm"
          }
          _focus={{
            boxShadow: `2px 2px 8px 0px rgba(0, 0, 0, 0.14)`,
            _dark: {
              boxShadow: `2px 2px 8px 0px rgba(54, 94, 114, 0.4)`,
            },
          }}
        >
          <PopoverArrow bg={bg} />
          <PopoverCloseButton />
          <PopoverHeader borderColor={borderColor} fontWeight="bold">
            {term}
          </PopoverHeader>
          <PopoverBody fontSize="0.9em" lineHeight="1.3em" px="4" pb="4">
            <Stack spacing="2">
              <ReactMarkdown
                components={{
                  code: InlineCode,
                  pre: (props) => <MarkdownCodeBlock {...props} disableCopy />,
                  a: (props) => (
                    <Link
                      color="blue.400"
                      _dark={{ color: "navy.100" }}
                      target="_blank"
                      {...props}
                    />
                  ),
                  ul: (props) => (
                    <div>
                      <UnorderedList {...props} />
                    </div>
                  ),
                  ol: (props) => (
                    <div>
                      <OrderedList {...props} />
                    </div>
                  ),
                  li: ListItem,
                }}
              >
                {definition}
              </ReactMarkdown>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
